/**
 * Licences tab: every Pax8 subscription against what Ewee Inc bills in Books (#1111, #1112, #1119). Pax8 is the
 * source of truth for the licence and its quantity. Grouped columns Quantity | Price vs MSRP | Today | At renewal |
 * Books | Notes; values are coloured (one legend) instead of a tag column, and every coloured value carries its
 * formula and source numbers in a tooltip.
 */
import { EmptyState } from '@patchbay/ui'
import { useQuery } from '@tanstack/react-query'
import * as React from 'react'
import type { ReconLineOut } from '../../lib/api/generated'
import { Tile } from '../profit/format'
import { QueryView } from '../profit/states'
import {
  bandOf,
  cad,
  cadCompact,
  customerLabel,
  DASH_CELL,
  dayLabel,
  daysFrom,
  ExtLink,
  FilterChips,
  type GroupedColumn,
  GroupedTable,
  Legend,
  MarginValue,
  MsrpValue,
  msrpDelta,
  num,
  pax8Url,
  pct1,
  qty,
  reconQuery,
  SearchBox,
  termLabel,
} from './common'
import { NotesButton } from './notes'

export type LicenceFilter =
  | 'action'
  | 'neg_now'
  | 'neg_renewal'
  | 'under_5'
  | 'under_10'
  | 'under_20'
  | 'msrp'
  | 'fix'
  | 'not_billed'
  | 'unmapped'
  | 'noted'
  | 'all'

/** A line we bill and can compare: billed, not pooled usage on Ewee's own company. */
export const isBilled = (r: ReconLineOut): boolean => num(r.billed_rate) !== null && !(r.flags ?? []).includes('pooled_usage')

const notBilled = (r: ReconLineOut) => {
  const f = r.flags ?? []
  return f.includes('not_billed') || f.includes('no_pax8_subscription')
}
const qtyDiffers = (r: ReconLineOut) => {
  const a = num(r.pax8_quantity)
  const b = num(r.billed_quantity)
  return (r.flags ?? []).includes('qty_mismatch') || (a !== null && b !== null && a !== b)
}
const marginNow = (r: ReconLineOut) => num(r.margin_pct_now)
const marginRenewal = (r: ReconLineOut) => num(r.margin_pct_renewal)
const worstMargin = (r: ReconLineOut) => {
  const xs = [marginNow(r), marginRenewal(r)].filter((x): x is number => x !== null)
  return xs.length ? Math.min(...xs) : null
}
const inBand = (lo: number, hi: number) => (r: ReconLineOut) => {
  const m = worstMargin(r)
  return isBilled(r) && m !== null && m >= lo && m < hi
}
const underMsrp = (r: ReconLineOut) => {
  const d = msrpDelta(r.msrp_delta_pct, r.billed_rate, r.msrp)
  return isBilled(r) && d !== null && d < 0
}

export const LICENCE_FILTERS: { id: LicenceFilter; label: string; test: (r: ReconLineOut) => boolean }[] = [
  {
    id: 'action',
    label: 'Needs action',
    test: (r) =>
      (isBilled(r) && (worstMargin(r) ?? 100) < 10) ||
      notBilled(r) ||
      (r.books_fix ?? []).length > 0 ||
      (r.flags ?? []).includes('unmapped') ||
      (r.flags ?? []).includes('price_reset_suspect'),
  },
  { id: 'neg_now', label: 'Negative now', test: (r) => isBilled(r) && (marginNow(r) ?? 0) < 0 },
  { id: 'neg_renewal', label: 'Negative at renewal', test: (r) => isBilled(r) && (marginRenewal(r) ?? 0) < 0 },
  { id: 'under_5', label: 'Under 5%', test: inBand(0, 5) },
  { id: 'under_10', label: 'Under 10%', test: inBand(5, 10) },
  { id: 'under_20', label: 'Under 20%', test: inBand(10, 20) },
  { id: 'msrp', label: 'Under MSRP', test: underMsrp },
  { id: 'fix', label: 'Books to fix', test: (r) => (r.books_fix ?? []).length > 0 },
  { id: 'not_billed', label: 'Not billed / not in Pax8', test: notBilled },
  { id: 'unmapped', label: 'Unmapped', test: (r) => (r.flags ?? []).includes('unmapped') },
  { id: 'noted', label: 'Has a note', test: (r) => (r.notes_count ?? 0) > 0 },
  { id: 'all', label: 'All', test: () => true },
]

/** Annual billing quotes per seat per year; the rate fields are monthly-equivalent. */
const unitLabel = 'per seat per month'
const seats = (r: ReconLineOut) => num(r.billed_quantity) ?? num(r.pax8_quantity)

export function monthlyBilled(r: ReconLineOut): number | null {
  const rate = num(r.billed_rate)
  const n = num(r.billed_quantity)
  return rate === null || n === null ? null : rate * n
}

export function monthlyCost(r: ReconLineOut, when: 'now' | 'renewal'): number | null {
  const unit = num(when === 'now' ? r.unit_cost_now : (r.unit_cost_renewal ?? r.unit_cost_now))
  const n = num(r.pax8_quantity)
  return unit === null || n === null ? null : unit * n
}

/** Sum a figure over lines; null (not zero) when every line withheld it. */
export function total(rows: ReconLineOut[], pick: (r: ReconLineOut) => number | null): number | null {
  let seen = false
  let sum = 0
  for (const r of rows) {
    const v = pick(r)
    if (v !== null) {
      seen = true
      sum += v
    }
  }
  return seen ? sum : null
}

function LicenceCell({ r }: { r: ReconLineOut }) {
  const fix = r.books_fix ?? []
  const flags = r.flags ?? []
  return (
    <div className="min-w-48 max-w-80">
      <ExtLink href={pax8Url(r.pax8_company_id, r.links?.pax8)} className="font-medium">
        {r.product_name ?? r.sku ?? 'Unnamed licence'}
      </ExtLink>
      <span className="block text-xs text-muted-foreground">
        {[termLabel(r), r.sku, r.subscription_status && r.subscription_status !== 'Active' ? r.subscription_status : null]
          .filter(Boolean)
          .join(' · ')}
      </span>
      {flags.includes('price_reset_suspect') ? (
        <span className="block text-xs text-destructive">Pax8 price may have reset to default: check the customer price</span>
      ) : null}
      {fix.map((f) => (
        <span key={`${f.code}-${f.detail}`} className="block text-xs text-warning">
          Books: {f.detail}
        </span>
      ))}
    </div>
  )
}

function CustomerCell({ r }: { r: ReconLineOut }) {
  const unmapped = (r.flags ?? []).includes('unmapped')
  return (
    <div className="min-w-36 max-w-60">
      <ExtLink href={r.links?.crm} className={unmapped ? 'text-muted-foreground' : 'font-medium'}>
        {customerLabel(r)}
      </ExtLink>
      {unmapped ? <span className="block text-xs text-warning">Not mapped to a customer yet</span> : null}
    </div>
  )
}

export function licenceColumns(now: Date): GroupedColumn<ReconLineOut>[] {
  return [
    { id: 'customer', header: 'Customer', group: '', cell: (r) => <CustomerCell r={r} />, sortValue: customerLabel },
    { id: 'licence', header: 'Licence', group: '', cell: (r) => <LicenceCell r={r} />, sortValue: (r) => r.product_name ?? '' },
    {
      id: 'pax8_qty',
      header: 'Pax8',
      group: 'Quantity',
      numeric: true,
      cell: (r) => qty(r.pax8_quantity),
      sortValue: (r) => num(r.pax8_quantity),
    },
    {
      id: 'billed_qty',
      header: 'Billed',
      group: 'Quantity',
      numeric: true,
      sortValue: (r) => num(r.billed_quantity),
      cell: (r) =>
        num(r.billed_quantity) === null ? (
          DASH_CELL
        ) : (
          <ExtLink
            href={r.links?.books_invoice}
            className={qtyDiffers(r) ? 'font-semibold text-destructive' : undefined}
            label={qtyDiffers(r) ? `Books bills ${qty(r.billed_quantity)}, Pax8 has ${qty(r.pax8_quantity)}` : undefined}
          >
            {qty(r.billed_quantity)}
          </ExtLink>
        ),
    },
    {
      id: 'billed_rate',
      header: 'We bill',
      group: 'Price vs MSRP',
      numeric: true,
      sortValue: (r) => num(r.billed_rate),
      cell: (r) =>
        notBilled(r) ? (
          <span className="font-sans font-medium text-destructive">
            {(r.flags ?? []).includes('no_pax8_subscription') ? 'not in Pax8' : 'not billed'}
          </span>
        ) : (
          <ExtLink href={r.links?.books_invoice}>{cad(r.billed_rate)}</ExtLink>
        ),
    },
    { id: 'msrp', header: 'MSRP', group: 'Price vs MSRP', numeric: true, cell: (r) => cad(r.msrp), sortValue: (r) => num(r.msrp) },
    {
      id: 'msrp_delta',
      header: '± MSRP',
      group: 'Price vs MSRP',
      numeric: true,
      sortValue: (r) => (isBilled(r) ? msrpDelta(r.msrp_delta_pct, r.billed_rate, r.msrp) : null),
      cell: (r) =>
        isBilled(r) ? <MsrpValue server={r.msrp_delta_pct} billed={r.billed_rate} msrp={r.msrp} unit={unitLabel} /> : DASH_CELL,
    },
    {
      id: 'cost_now',
      header: 'Cost',
      group: 'Today',
      numeric: true,
      cell: (r) => cad(r.unit_cost_now),
      sortValue: (r) => num(r.unit_cost_now),
    },
    {
      id: 'margin_now',
      header: 'Margin',
      group: 'Today',
      numeric: true,
      sortValue: marginNow,
      cell: (r) =>
        isBilled(r) ? (
          <MarginValue
            when="Today"
            billed={r.billed_rate}
            cost={r.unit_cost_now}
            margin={r.margin_pct_now}
            band={r.band_now}
            seats={seats(r)}
            unit={unitLabel}
          />
        ) : (
          DASH_CELL
        ),
    },
    {
      id: 'renews',
      header: 'Renews',
      group: 'At renewal',
      sortValue: (r) => r.renewal_date ?? null,
      cell: (r) => {
        const d = daysFrom(r.renewal_date, now)
        return (
          <span className="whitespace-nowrap">
            {dayLabel(r.renewal_date)}
            {d !== null && d >= 0 && d <= 90 ? <span className="block text-xs text-muted-foreground">in {d} days</span> : null}
          </span>
        )
      },
    },
    {
      id: 'cost_renewal',
      header: 'Cost',
      group: 'At renewal',
      numeric: true,
      sortValue: (r) => num(r.unit_cost_renewal),
      cell: (r) => {
        const a = num(r.unit_cost_now)
        const b = num(r.unit_cost_renewal)
        return (
          <span className={a !== null && b !== null && b > a + 0.005 ? 'text-destructive' : undefined}>{cad(r.unit_cost_renewal)}</span>
        )
      },
    },
    {
      id: 'margin_renewal',
      header: 'Margin',
      group: 'At renewal',
      numeric: true,
      sortValue: marginRenewal,
      cell: (r) =>
        isBilled(r) ? (
          <MarginValue
            when={`From ${dayLabel(r.renewal_date)}`}
            billed={r.billed_rate}
            cost={r.unit_cost_renewal}
            margin={r.margin_pct_renewal}
            band={r.band_renewal}
            seats={seats(r)}
            unit={unitLabel}
          />
        ) : (
          DASH_CELL
        ),
    },
    {
      id: 'books_customer',
      header: 'Books customer',
      group: 'Books',
      narrow: true,
      sortValue: (r) => r.books_customer_name ?? null,
      cell: (r) => <ExtLink href={r.links?.books_customer}>{r.books_customer_name ?? '—'}</ExtLink>,
    },
    {
      id: 'notes',
      header: 'Notes',
      group: ' ',
      sortValue: (r) => r.notes_count ?? 0,
      cell: (r) => (
        <NotesButton
          lineKey={r.line_key}
          count={r.notes_count ?? 0}
          title={`${customerLabel(r)} · ${r.product_name ?? r.sku ?? 'licence'}`}
          subtitle={termLabel(r)}
        />
      ),
    },
  ]
}

function Stats({ lines }: { lines: ReconLineOut[] }) {
  const billed = lines.filter(isBilled)
  const rev = total(billed, monthlyBilled)
  const c1 = total(billed, (r) => monthlyCost(r, 'now'))
  const c2 = total(billed, (r) => monthlyCost(r, 'renewal'))
  const m1 = rev && c1 !== null ? ((rev - c1) / rev) * 100 : null
  const m2 = rev && c2 !== null ? ((rev - c2) / rev) * 100 : null
  const negNow = billed.filter((r) => bandOf(r.band_now, r.margin_pct_now) === 'negative').length
  const negRen = billed.filter((r) => bandOf(r.band_renewal, r.margin_pct_renewal) === 'negative').length
  const costVisible = c1 !== null
  const toFix = lines.filter((r) => (r.books_fix ?? []).length > 0).length
  return (
    <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,10rem),1fr))] gap-2.5">
      <Tile label="Billed per month" value={cadCompact(rev)} />
      <Tile label="Cost now" value={cadCompact(c1)} />
      <Tile label="Margin now" value={pct1(m1)} tone={m1 !== null && m1 < 0 ? 'danger' : undefined} />
      <Tile label="Cost after renewals" value={cadCompact(c2)} />
      <Tile label="Margin after renewals" value={pct1(m2)} tone={m1 !== null && m2 !== null && m2 < m1 ? 'warning' : undefined} />
      <Tile label="Negative now" value={costVisible ? negNow : '—'} tone={negNow ? 'danger' : undefined} />
      <Tile label="Negative at renewal" value={costVisible ? negRen : '—'} tone={negRen ? 'warning' : undefined} />
      <Tile label="Under MSRP" value={billed.filter(underMsrp).length} />
      <Tile label="Books lines to fix" value={toFix} tone={toFix ? 'warning' : undefined} />
    </div>
  )
}

export function matchesSearch(r: ReconLineOut, q: string): boolean {
  const term = q.trim().toLowerCase()
  if (!term) return true
  return [r.company_name, r.books_customer_name, r.product_name, r.sku, r.usage_family, ...(r.books_fix ?? []).map((f) => f.detail)]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
    .includes(term)
}

export function LicencesTab({ filter, onFilter }: { filter: LicenceFilter; onFilter: (f: LicenceFilter) => void }) {
  const q = useQuery(reconQuery())
  const [search, setSearch] = React.useState('')
  const columns = React.useMemo(() => licenceColumns(new Date()), [])
  return (
    <QueryView query={q} what="Licences">
      {(data) => {
        const lines = (data.lines ?? []).filter((r) => r.kind === 'licence')
        if (!data.computed_at) {
          return <EmptyState title="No reconciliation has run yet." detail="The nightly Pax8 and Books read builds this list." />
        }
        const test = LICENCE_FILTERS.find((f) => f.id === filter)?.test ?? (() => true)
        const rows = lines.filter((r) => test(r) && matchesSearch(r, search))
        return (
          <div className="space-y-3.5">
            <Stats lines={lines} />
            <FilterChips
              label="Licence filters"
              value={filter}
              onChange={onFilter}
              options={LICENCE_FILTERS.map((f) => ({ id: f.id, label: f.label, count: lines.filter(f.test).length }))}
            />
            <SearchBox
              value={search}
              onChange={setSearch}
              label="Search customer, licence, Books fix"
              count={`${rows.length} of ${lines.length} lines`}
            />
            <Legend />
            <GroupedTable
              label="Licences"
              columns={columns}
              rows={rows}
              rowKey={(r) => r.line_key}
              initialSort={{ id: 'renews', dir: 1 }}
              rowProps={(r) => ({ 'data-line': r.line_key })}
              empty={<EmptyState title="No licence matches this filter." />}
            />
          </div>
        )
      }}
    </QueryView>
  )
}
